(() => {
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  const PICKER_SELECTOR = '.js-variants';
  const SECTION_SELECTOR = '[id^="main-product-"]';
  const sectionSyncTimers = new WeakMap();
  const storeAvailabilityByStock = new WeakMap();
  const STOREFRONT_API_VERSION = '2026-07';
  const VARIANT_STORE_AVAILABILITY_QUERY = `
    query VariantStoreAvailability($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          storeAvailability(first: 100) {
            nodes {
              quantityAvailable
              location {
                id
              }
            }
          }
        }
      }
    }
  `;

  const createStoreAvailabilityState = () => ({
    variantId: null,
    generation: 0,
    status: 'idle',
    abortController: null,
    pendingPromise: null,
  });

  const getStoreAvailabilityState = (stock) => {
    let state = storeAvailabilityByStock.get(stock);
    if (!state) {
      state = createStoreAvailabilityState();
      storeAvailabilityByStock.set(stock, state);
    }
    return state;
  };

  const escapeHtml = (value) => {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const isAbortError = (error) => {
    return (
      !!error &&
      (error.name === 'AbortError' || error.code === 20 || error.code === 'ABORT_ERR')
    );
  };

  const parseJson = (script, fallback) => {
    try {
      return JSON.parse(script.textContent || JSON.stringify(fallback));
    } catch {
      return fallback;
    }
  };

  const normalizeLocationId = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.split('/').pop() || raw;
  };

  const getStoreLocationsFromDom = (stock) => {
    const script = stock.querySelector('script[data-store-locations]');
    const locations = script ? parseJson(script, []) : [];
    return Array.isArray(locations) ? locations : [];
  };

  const fetchStorefrontAvailability = (stock, variantId, signal) => {
    const token = stock.dataset.storefrontToken;
    const domain = stock.dataset.shopDomain;

    if (!token || !domain) {
      return Promise.resolve([]);
    }

    return fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: VARIANT_STORE_AVAILABILITY_QUERY,
        variables: {
          id: `gid://shopify/ProductVariant/${variantId}`,
        },
      }),
      signal,
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Storefront API request failed (${response.status})`);
        }

        return response.json();
      })
      .then((payload) => {
        if (payload.errors && payload.errors.length) {
          throw new Error(payload.errors[0].message || 'Storefront API GraphQL error');
        }

        const nodes =
          payload.data &&
          payload.data.node &&
          payload.data.node.storeAvailability &&
          payload.data.node.storeAvailability.nodes;

        if (!Array.isArray(nodes)) return [];

        return nodes.map((node) => ({
          id: (node.location && node.location.id) || '',
          quantityAvailable: Number(node.quantityAvailable) || 0,
        }));
      });
  };

  const mergeLocationCatalogWithAvailability = (catalog, availabilities) => {
    const availabilityById = new Map();

    availabilities.forEach((entry) => {
      const idKey = normalizeLocationId(entry.id);
      if (idKey) availabilityById.set(idKey, entry);
    });

    return catalog.map((location) => {
      const availability = availabilityById.get(normalizeLocationId(location.id));
      const quantityAvailable = availability ? Number(availability.quantityAvailable) || 0 : 0;

      return {
        ...location,
        available: quantityAvailable > 0,
        quantityAvailable,
      };
    });
  };

  const fetchStoreLocations = (stock, variant, signal) => {
    const catalog = getStoreLocationsFromDom(stock);
    const variantId = variant && variant.id ? String(variant.id) : '';

    if (!catalog.length || !variantId) {
      return Promise.resolve([]);
    }

    return fetchStorefrontAvailability(stock, variantId, signal).then((availabilities) => {
      return mergeLocationCatalogWithAvailability(catalog, availabilities);
    });
  };

  const getVariantStrings = () => {
    return window.variantStrings || {};
  };

  const getSectionId = (section) => {
    return section && section.id ? section.id.replace('main-product-', '') : '';
  };

  const getVariantData = (picker) => {
    const script = picker ? picker.querySelector('script[data-variants]') : null;
    if (!script) return [];

    return parseJson(script, []);
  };

  const getSelectedOptions = (picker) => {
    const fieldsets = Array.from(picker.querySelectorAll('.m-variants__fieldset:not(.a-hide)'));

    return fieldsets.reduce((options, fieldset, index) => {
      const position = Number(fieldset.dataset.optionPosition || index + 1);
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      const selectInput = fieldset.querySelector('select');
      const selectedValue = checkedInput ? checkedInput.value : selectInput ? selectInput.value : '';

      if (selectedValue) {
        options[position - 1] = selectedValue;
      }

      return options;
    }, []);
  };

  const isBundleablePicker = (picker) => {
    return !!(picker && picker.dataset.bundleable === 'true');
  };

  const getSelectedVariant = (picker) => {
    const variants = getVariantData(picker);
    const selectedOptions = getSelectedOptions(picker);

    if (!variants.length || !selectedOptions.length) return null;

    return variants.find((variant) => {
      return selectedOptions.every((value, index) => {
        return value === undefined || variant.options[index] === value;
      });
    }) || null;
  };

  const getActiveVariant = (section) => {
    if (!section) return null;

    const picker = section.querySelector(PICKER_SELECTOR);
    if (picker && !isBundleablePicker(picker)) {
      const selected = getSelectedVariant(picker);
      if (selected) return selected;
    }

    const form = section.querySelector(FORM_SELECTOR);
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const stock = section.querySelector('.o-product__stock');
    const variantId =
      (idInput && idInput.value) || (stock && stock.dataset.variantId) || '';

    return variantId ? { id: Number(variantId) } : null;
  };

  const getAvailableButtonText = (button) => {
    const strings = getVariantStrings();

    if (button && button.dataset.pickupOnly === 'true') {
      return strings.pickupOnly || strings.addToCart || '';
    }

    return strings.addToCart || '';
  };

  const setButtonState = (button, variant) => {
    if (!button || button.dataset.bundleable === 'true') return;

    const strings = getVariantStrings();
    const text = button.querySelector('.m-product-form__submit-text, .m-action-bar__submit-text');
    const isAvailable = !!(variant && variant.available);
    const buttonText = variant ? (isAvailable ? getAvailableButtonText(button) : strings.soldOut) : strings.soldOut;

    button.toggleAttribute('disabled', !isAvailable);
    button.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');

    if (text && buttonText) {
      text.textContent = buttonText;
    }
  };

  const syncActionBar = (section, variant) => {
    const sectionId = getSectionId(section);
    const strings = getVariantStrings();
    const isAvailable = !!(variant && variant.available);
    const text = variant ? (isAvailable ? strings.addToCart : strings.soldOut) : strings.soldOut;

    if (window.WhatSub && window.WhatSub.dispatch) {
      window.WhatSub.dispatch('SET_ACTION_BAR_BUTTON', {
        section: sectionId,
        disable: !isAvailable,
        text,
      });
    }

    const actionBar = document.getElementById(`product-action-bar-${sectionId}`);
    const actionButton = actionBar ? actionBar.querySelector('.js-action-bar-button') : null;
    setButtonState(actionButton, variant);
  };

  const syncBackInStock = (section, variant) => {
    const backInStock = section.querySelector('.m-back-in-stock');
    const backInStockInner = backInStock ? backInStock.querySelector('.m-back-in-stock__inner') : null;
    const variantInput = backInStock ? backInStock.querySelector('.variant_id') : null;
    const shouldShowBackInStock = !!(variant && !variant.available);

    if (!backInStockInner) return;

    backInStockInner.classList.toggle('hide', !shouldShowBackInStock);

    if (variantInput && variant) {
      variantInput.value = variant.id;
      variantInput.setAttribute('value', variant.id);
    }
  };

  const syncStock = (section, variant) => {
    const stock = section.querySelector('.o-product__stock');
    const stockStatus = stock ? stock.querySelector('.m-stock') : null;
    const stockText = stock ? stock.querySelector('.m-stock__text') : null;
    const stockDataScript = stock ? stock.querySelector('script[data-variant-stock]') : null;
    const stockData = stockDataScript ? parseJson(stockDataScript, {}) : {};
    const selectedStock = variant ? stockData[String(variant.id)] : null;
    const strings = getVariantStrings();
    const outOfStockText = (stock && stock.dataset.outOfStockText) || strings.soldOut || '';

    if (!stockStatus || !stockText) return;

    if (variant && variant.id) {
      stock.dataset.variantId = String(variant.id);
    }

    stockStatus.classList.remove(
      'm-stock--in-stock',
      'm-stock--low-stock',
      'm-stock--available-to-order',
      'm-stock--no-stock',
      'm-stock--delivery-time'
    );

    if (selectedStock) {
      stockStatus.classList.add(selectedStock.className);
      stockText.textContent = selectedStock.text;
    } else {
      stockStatus.classList.add('m-stock--no-stock');
      stockText.textContent = outOfStockText;
    }

    refreshStoreAvailabilityIfOpen(stock, variant);
  };

  const getProductFormQuantity = (section) => {
    const quantityInput = section.querySelector(`${FORM_SELECTOR} input[name="quantity"]`);
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getProductFormErrorElements = (section) => {
    const form = section.querySelector(FORM_SELECTOR);
    return {
      wrapper: form ? form.querySelector('.m-product-form__error-message-wrapper') : null,
      messageEl: form ? form.querySelector('.m-product-form__error-message') : null,
    };
  };

  const clearProductFormError = (section) => {
    const { wrapper, messageEl } = getProductFormErrorElements(section);
    if (!wrapper || !messageEl) return;

    wrapper.hidden = true;
    messageEl.textContent = '';
  };

  const closeStoreAvailabilityModal = (stock) => {
    const { storeTrigger } = getStoreAvailabilityElements(stock);
    if (!storeTrigger) return;

    const modal = resolveModalTarget(storeTrigger.getAttribute('data-modal-target'));
    if (!modal) return;

    modal.classList.remove('modal--open');

    if (!document.querySelector('.modal.modal--open')) {
      document.documentElement.classList.remove('has-open-modal');
    }
  };

  const showStorePickupError = (section, message) => {
    const { wrapper, messageEl } = getProductFormErrorElements(section);

    if (wrapper && messageEl) {
      messageEl.textContent = message;
      wrapper.hidden = false;
      return;
    }

    window.alert(message);
  };

  const setStorePickupButtonLoading = (button, isLoading) => {
    if (!button) return;

    const text = button.querySelector('.m-stock__store-pickup-text');
    const loader = button.querySelector('.m-stock__store-pickup-loader');

    button.classList.toggle('m-stock__store-pickup--loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');

    if (text) {
      text.classList.toggle('hide', isLoading);
    }

    if (loader) {
      loader.classList.toggle('hide', !isLoading);
    }
  };

  const resetStorePickupButtonLoading = (button, observers) => {
    setStorePickupButtonLoading(button, false);

    if (!observers) return;

    if (observers.addToCartUnsubscribe) {
      observers.addToCartUnsubscribe();
    }

    if (observers.errorObserver) {
      observers.errorObserver.disconnect();
    }

    if (observers.timeoutId) {
      window.clearTimeout(observers.timeoutId);
    }
  };

  const addToCartForStorePickup = (section, stock, variantId, button) => {
    const form = section.querySelector(FORM_SELECTOR);
    const submitButton = form ? form.querySelector('.m-product-form__submit') : null;
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const quantityInput = form ? form.querySelector('input[name="quantity"]') : null;

    if (!form || !submitButton || !idInput) {
      return Promise.reject(new Error('Product form is unavailable'));
    }

    idInput.value = variantId;
    idInput.setAttribute('value', variantId);

    if (quantityInput) {
      quantityInput.value = String(getProductFormQuantity(section));
    }

    clearProductFormError(section);
    closeStoreAvailabilityModal(stock);
    setStorePickupButtonLoading(button, true);

    form.dataset.pickupAddToCart = 'true';

    const wasDisabled = submitButton.hasAttribute('disabled');
    if (wasDisabled) {
      submitButton.removeAttribute('disabled');
      submitButton.setAttribute('aria-disabled', 'false');
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const observers = {};

      const finish = (error) => {
        if (settled) return;
        settled = true;

        form.removeAttribute('data-pickup-add-to-cart');

        if (wasDisabled) {
          submitButton.setAttribute('disabled', '');
          submitButton.setAttribute('aria-disabled', 'true');
        }

        resetStorePickupButtonLoading(button, observers);

        if (error) {
          reject(error);
          return;
        }

        resolve();
      };

      observers.timeoutId = window.setTimeout(() => {
        finish(new Error((window.cartStrings && window.cartStrings.error) || 'Unable to add item to cart'));
      }, 30000);

      if (window.WhatSub && window.WhatSub.subscribe) {
        const onAddToCart = () => {
          if (window.WhatSub.unsubscribe) {
            window.WhatSub.unsubscribe('ADD_TO_CART', onAddToCart);
          }
          finish();
        };

        observers.addToCartUnsubscribe = () => {
          if (window.WhatSub.unsubscribe) {
            window.WhatSub.unsubscribe('ADD_TO_CART', onAddToCart);
          }
        };

        window.WhatSub.subscribe('ADD_TO_CART', onAddToCart);
      }

      const errorWrapper = form.querySelector('.m-product-form__error-message-wrapper');
      if (errorWrapper && window.MutationObserver) {
        observers.errorObserver = new MutationObserver(() => {
          if (!errorWrapper.hidden) {
            const messageEl = form.querySelector('.m-product-form__error-message');
            finish(new Error((messageEl && messageEl.textContent) || 'Unable to add item to cart'));
          }
        });

        observers.errorObserver.observe(errorWrapper, {
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }

      form.requestSubmit(submitButton);
    });
  };

  const renderStorePickupButton = (options) => {
    if (!options.hasAnyStoreStock || !options.variantId || !options.pickupLabel) {
      return '';
    }

    return (
      '<div class="m-stock__store-actions">' +
        `<button type="button" class="m-stock__store-pickup a-btn a-btn--primary js-store-pickup-button" data-variant-id="${escapeHtml(options.variantId)}">` +
          `<span class="m-stock__store-pickup-text">${escapeHtml(options.pickupLabel)}</span>` +
          '<span class="m-stock__store-pickup-loader hide" aria-hidden="true">…</span>' +
        '</button>' +
      '</div>'
    );
  };

  const renderStoreLocationItem = (location, options) => {
    const shopEmail = options.shopEmail || '';
    const showPhone = options.showPhone;
    const showEmail = options.showEmail;
    const isAvailable = location.available === true;
    const pickupButton = renderStorePickupButton(options);
    const itemClass = isAvailable
      ? 'm-stock__store-item m-stock__store-item--available'
      : 'm-stock__store-item m-stock__store-item--unavailable';
    const statusClass = isAvailable
      ? 'm-stock__store-status m-stock__store-status--available'
      : 'm-stock__store-status m-stock__store-status--unavailable';
    const statusText = isAvailable ? options.availableText : options.unavailableText;
    const addressHtml = location.addressHtml || '';
    const phone = showPhone ? location.phone || '' : '';
    const email = showEmail ? shopEmail : '';
    const hasContactDetails = !!(addressHtml || phone || email);

    if (!hasContactDetails) {
      return (
        `<li class="${itemClass}">` +
          '<div class="m-stock__store-summary m-stock__store-summary--static">' +
            `<span class="${statusClass}" aria-hidden="true"></span>` +
            `<span class="m-stock__store-name">${escapeHtml(location.name)}</span>` +
            `<span class="m-stock__store-availability">${escapeHtml(statusText)}</span>` +
          '</div>' +
          pickupButton +
        '</li>'
      );
    }

    const contactLines = [
      addressHtml ? `<div class="m-stock__store-address rte">${addressHtml}</div>` : '',
      phone
        ? `<p class="m-stock__store-phone"><a href="tel:${escapeHtml(phone)}">${escapeHtml(phone)}</a></p>`
        : '',
      email
        ? `<p class="m-stock__store-email"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
        : '',
    ].join('');

    return (
      `<li class="${itemClass}">` +
        '<details class="m-stock__store-details">' +
          '<summary class="m-stock__store-summary">' +
            `<span class="${statusClass}" aria-hidden="true"></span>` +
            `<span class="m-stock__store-name">${escapeHtml(location.name)}</span>` +
            `<span class="m-stock__store-availability">${escapeHtml(statusText)}</span>` +
            '<span class="m-stock__store-chevron" aria-hidden="true">' +
              '<svg class="icon icon-chevron-right" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
                '<path fill-rule="evenodd" clip-rule="evenodd" d="M7.64645 3.64645C7.84171 3.45118 8.15829 3.45118 8.35355 3.64645L16.3536 11.6464C16.5488 11.8417 16.5488 12.1583 16.3536 12.3536L8.35355 20.3536C8.15829 20.5488 7.84171 20.5488 7.64645 20.3536C7.45118 20.1583 7.45118 19.8417 7.64645 19.6464L15.2929 12L7.64645 4.35355C7.45118 4.15829 7.45118 3.84171 7.64645 3.64645Z" fill="currentColor"/>' +
              '</svg>' +
            '</span>' +
          '</summary>' +
          `<div class="m-stock__store-contact">${contactLines}${pickupButton}</div>` +
        '</details>' +
      '</li>'
    );
  };

  const getStoreDisplayOptions = (stock) => {
    return {
      availableText: stock.dataset.storeAvailableText || 'In stock',
      unavailableText: stock.dataset.storeUnavailableText || 'Out of stock',
      shopEmail: stock.dataset.storeContactEmail || '',
      showPhone: stock.dataset.showStorePhone !== 'false',
      showEmail: stock.dataset.showStoreEmail !== 'false',
      pickupLabel: stock.dataset.storePickupLabel || 'Pick up here',
      variantId: stock.dataset.storePickupVariantId || '',
    };
  };

  const resolveModalTarget = (targetSelector) => {
    if (!targetSelector) return null;

    const modals = Array.from(document.querySelectorAll(targetSelector));
    if (!modals.length) return null;

    const modal = modals[modals.length - 1];

    modals.slice(0, -1).forEach((duplicate) => {
      if (duplicate.classList.contains('js-stock-availability-modal')) {
        duplicate.remove();
      }
    });

    return modal;
  };

  const getStoreAvailabilityElements = (stock) => {
    const storeTrigger = stock.querySelector('.js-stock-availability-trigger');
    if (!storeTrigger) {
      return { storeTrigger: null, storePanel: null };
    }

    const modalTarget = storeTrigger.getAttribute('data-modal-target');
    const modal = resolveModalTarget(modalTarget);
    const storePanel =
      (modal && modal.querySelector('.js-stock-stores-panel')) ||
      stock.querySelector('.js-stock-stores-panel');

    return { storeTrigger, storePanel };
  };

  const renderStoreLocations = (stock, locations) => {
    const { storeTrigger, storePanel } = getStoreAvailabilityElements(stock);
    const displayOptions = getStoreDisplayOptions(stock);

    if (!storeTrigger || !storePanel) return;

    storePanel.classList.remove('is-loading');

    if (!locations.length) {
      storeTrigger.hidden = true;
      storePanel.innerHTML = '';
      return;
    }

    const variantId = getStoreAvailabilityState(stock).variantId || '';
    const section = stock.closest(SECTION_SELECTOR);
    const allowPickup = !isBundleablePicker(section && section.querySelector(PICKER_SELECTOR));
    const hasAnyStoreStock = locations.some((location) => location.available);

    stock.dataset.storePickupVariantId = variantId;
    const renderOptions = {
      ...displayOptions,
      variantId,
      pickupLabel: allowPickup ? displayOptions.pickupLabel : '',
      hasAnyStoreStock,
    };

    const listItems = locations
      .map((location) => renderStoreLocationItem(location, renderOptions))
      .join('');

    storeTrigger.hidden = false;
    storeTrigger.disabled = false;
    storeTrigger.setAttribute('aria-disabled', 'false');
    storePanel.innerHTML = `<ul class="m-stock__store-list" role="list">${listItems}</ul>`;
  };

  const isStoreAvailabilityModalOpen = (stock) => {
    const { storeTrigger } = getStoreAvailabilityElements(stock);
    if (!storeTrigger) return false;

    const modal = resolveModalTarget(storeTrigger.getAttribute('data-modal-target'));
    return !!(modal && modal.classList.contains('modal--open'));
  };

  const refreshStoreAvailabilityIfOpen = (stock, variant) => {
    if (!isStoreAvailabilityModalOpen(stock)) return;
    loadStoreAvailability(stock, variant, { force: true });
  };

  const setStorePanelLoading = (stock, isLoading) => {
    const { storePanel } = getStoreAvailabilityElements(stock);
    if (!storePanel) return;

    storePanel.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      storePanel.innerHTML = '';
    }
  };

  const resetStoreLocations = (stock) => {
    const { storeTrigger, storePanel } = getStoreAvailabilityElements(stock);

    if (storeTrigger) {
      storeTrigger.hidden = false;
      storeTrigger.disabled = true;
      storeTrigger.setAttribute('aria-disabled', 'true');
    }

    if (storePanel) {
      storePanel.classList.remove('is-loading');
      storePanel.innerHTML = '';
    }
  };

  const abortInflightRequest = (state) => {
    if (state.pendingPromise) {
      void state.pendingPromise.catch(() => {});
    }

    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }
  };

  const loadStoreAvailability = (stock, variant, options = {}) => {
    const force = options.force === true;

    if (!stock || !stock.querySelector('.js-stock-availability-trigger')) {
      return Promise.resolve(false);
    }

    const variantId = variant && variant.id ? String(variant.id) : null;
    const state = getStoreAvailabilityState(stock);

    if (!variantId) {
      abortInflightRequest(state);
      state.variantId = null;
      state.status = 'idle';
      state.pendingPromise = null;
      resetStoreLocations(stock);
      return Promise.resolve(false);
    }

    if (!force && state.variantId === variantId && state.status === 'loading' && state.pendingPromise) {
      return state.pendingPromise;
    }

    abortInflightRequest(state);
    const abortController = new AbortController();
    state.abortController = abortController;
    state.variantId = variantId;
    state.generation += 1;
    const generation = state.generation;
    state.status = 'loading';

    setStorePanelLoading(stock, true);

    const promise = fetchStoreLocations(stock, variant, abortController.signal)
      .then((locations) => {
        if (generation !== state.generation) return false;
        if (state.variantId !== variantId) return false;
        state.status = 'ready';
        renderStoreLocations(stock, locations);
        return true;
      })
      .catch((error) => {
        if (isAbortError(error)) return false;
        if (generation !== state.generation) return false;
        state.status = 'error';
        renderStoreLocations(stock, []);
        return true;
      })
      .finally(() => {
        if (state.pendingPromise === promise) {
          state.pendingPromise = null;
        }
      });

    state.pendingPromise = promise;
    return promise;
  };

  const syncVariantState = (section) => {
    const picker = section.querySelector(PICKER_SELECTOR);
    if (!picker || isBundleablePicker(picker)) return;

    const variant = getSelectedVariant(picker);
    const form = section.querySelector(FORM_SELECTOR);
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const submitButton = form ? form.querySelector('.m-product-form__submit') : null;

    if (idInput && variant && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
      idInput.setAttribute('value', variant.id);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setButtonState(submitButton, variant);
    syncActionBar(section, variant);
    syncBackInStock(section, variant);
    syncStock(section, variant);

    return variant;
  };

  const queueSync = (section) => {
    const existingTimer = sectionSyncTimers.get(section);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timer = window.setTimeout(() => {
      sectionSyncTimers.delete(section);
      syncVariantState(section);
    }, 50);

    sectionSyncTimers.set(section, timer);
  };

  const getSectionFromPickupButton = (pickupButton) => {
    const modal = pickupButton.closest('.js-stock-availability-modal');
    if (!modal || !modal.id) return null;

    const sectionId = modal.id.replace(/^store-availability-/, '');
    if (!sectionId) return null;

    return document.getElementById(`main-product-${sectionId}`);
  };

  const getStockFromSection = (section) => {
    if (!section) return null;

    const sectionId = getSectionId(section);
    return document.getElementById(`stock-${sectionId}`) || section.querySelector('.o-product__stock');
  };

  const handleStorePickupClick = (event) => {
    const pickupButton = event.target.closest('.js-store-pickup-button');
    if (!pickupButton || !pickupButton.closest('.js-stock-availability-modal')) return;

    event.preventDefault();
    event.stopPropagation();

    const section = getSectionFromPickupButton(pickupButton);
    const stock = getStockFromSection(section);
    const form = section ? section.querySelector(FORM_SELECTOR) : null;
    const submitButton = form ? form.querySelector('.m-product-form__submit') : null;

    if (!section || !stock) return;
    if (isBundleablePicker(section.querySelector(PICKER_SELECTOR))) return;
    if (submitButton && submitButton.dataset.engraving === 'true') return;

    const variantId = pickupButton.dataset.variantId;
    if (!variantId) return;

    addToCartForStorePickup(section, stock, variantId, pickupButton).catch((error) => {
      const fallbackMessage =
        (window.cartStrings && window.cartStrings.error) || 'Unable to add item to cart';
      showStorePickupError(section, error && error.message ? error.message : fallbackMessage);
    });
  };

  const bindSection = (section) => {
    if (!section || section.dataset.customVariantAvailabilityInit === 'true') return;
    section.dataset.customVariantAvailabilityInit = 'true';

    const form = section.querySelector(FORM_SELECTOR);

    const onPickerChange = (event) => {
      if (event.target.closest(PICKER_SELECTOR)) {
        queueSync(section);
      }
    };

    section.addEventListener('change', onPickerChange);
    section.addEventListener('input', onPickerChange);

    section.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('.js-stock-availability-trigger[data-modal-target]');
        if (!trigger) return;

        const stock = section.querySelector('.o-product__stock');
        if (!stock) return;

        const variant = getActiveVariant(section);
        if (!variant) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }

        loadStoreAvailability(stock, variant, { force: true });
      },
      true
    );

    if (form) {
      const idInput = form.querySelector('input[name="id"]');

      form.addEventListener('submit', (event) => {
        const picker = section.querySelector(PICKER_SELECTOR);
        if (!picker || isBundleablePicker(picker)) return;
        if (form.dataset.pickupAddToCart === 'true') return;

        const variant = syncVariantState(section);
        if (!variant || !variant.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);

      if (idInput && window.MutationObserver) {
        const observer = new MutationObserver(() => queueSync(section));
        observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    queueSync(section);
  };

  const init = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(bindSection);
  };

  document.addEventListener('click', handleStorePickupClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('shopify:section:load', init);
})();
